/**
 * Browser error reporting, to the same Sentry project as the backend.
 *
 * Bookkeeper's pages report under stricter rules than Insure's: Layer data can appear in their URLs,
 * breadcrumbs and errors, so they send no personal data, no console or click breadcrumbs, and no URL
 * parameters. Each product links to the other with a full page load, so the page a document opens
 * on decides which rules it keeps.
 */
import * as Sentry from '@sentry/react'

import { isDevelopment, SENTRY_DSN } from '@/config/config'

/** Whether `pathname` is one of Bookkeeper's pages, Cai's AI CFO included, in any case, as the router matches it. */
function isBookkeeperPath(pathname: string): boolean {
  const path = pathname.toLowerCase()
  return ['/bookkeeper', '/cai-agent'].some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
}

function withoutUrlParameters(address: string): string {
  try {
    const url = new URL(address, window.location.origin)
    return url.origin + url.pathname
  } catch {
    return '[invalid URL]'
  }
}

const insureOptions: Sentry.BrowserOptions = {
  dsn: SENTRY_DSN,
  sendDefaultPii: true,
  environment: process.env.CAI_ENV,
  initialScope: { tags: { product: 'insure' } },
}

const bookkeeperOptions: Sentry.BrowserOptions = {
  dsn: SENTRY_DSN,
  environment: process.env.CAI_ENV,
  sendDefaultPii: false,
  initialScope: { tags: { product: 'bookkeeper', surface: 'frontend', error_source: 'bookkeeper' } },
  integrations: (defaults) => [
    ...defaults.filter(({ name }) => name !== 'BrowserSession' && name !== 'Breadcrumbs'),
    Sentry.breadcrumbsIntegration({ console: false, dom: false }),
  ],
  beforeBreadcrumb(breadcrumb) {
    const details = breadcrumb.data
    if (breadcrumb.category === 'navigation') {
      return {
        category: 'navigation',
        data: {
          from: typeof details?.from === 'string' ? withoutUrlParameters(details.from) : undefined,
          to: typeof details?.to === 'string' ? withoutUrlParameters(details.to) : undefined,
        },
      }
    }
    if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
      return {
        category: breadcrumb.category,
        type: 'http',
        data: {
          method: details?.method,
          status_code: details?.status_code,
          url: typeof details?.url === 'string' ? withoutUrlParameters(details.url) : undefined,
        },
      }
    }
    return null
  },
  beforeSend(event) {
    event.request = event.request?.url ? { url: withoutUrlParameters(event.request.url) } : undefined
    delete event.user
    delete event.extra
    if (event.message) event.message = event.message.replace(/https?:\/\/[^\s)]+/g, withoutUrlParameters)
    for (const exception of event.exception?.values ?? []) {
      if (exception.value)
        exception.value = exception.value.replace(/https?:\/\/[^\s)]+/g, withoutUrlParameters)
      for (const frame of exception.stacktrace?.frames ?? []) {
        if (frame.filename) frame.filename = withoutUrlParameters(frame.filename)
        if (frame.abs_path) frame.abs_path = withoutUrlParameters(frame.abs_path)
      }
    }
    return event
  },
}

/** Starts reporting everywhere but local development, under the rules of the product the page belongs to. */
export function startErrorReporting(): void {
  if (isDevelopment) return
  Sentry.init(isBookkeeperPath(window.location.pathname) ? bookkeeperOptions : insureOptions)
}
